import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { VoteChoice } from '@/components/voting';
import { apiRequest } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { logError } from '@/lib/logger';

import type { MatchPair } from '../types';

interface UseVoteSubmissionProps {
  runId: string | undefined;
  readyMatches: MatchPair[];
  effectiveTotalMatches: number;
  currentIndex: number;
  currentMatch: MatchPair | null;
  pendingVotes: Map<number, VoteChoice>;
  setPendingVotes: React.Dispatch<
    React.SetStateAction<Map<number, VoteChoice>>
  >;
  markVoted: (index: number) => void;
  next: () => void;
  goTo: (index: number) => void;
  canGoNext: boolean;
}

/**
 * Hook to manage vote submission logic and navigation
 */
export function useVoteSubmission({
  runId,
  readyMatches,
  effectiveTotalMatches,
  currentIndex,
  currentMatch,
  pendingVotes,
  setPendingVotes,
  markVoted,
  next,
  goTo,
  canGoNext,
}: UseVoteSubmissionProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentSelection, setCurrentSelection] = useState<VoteChoice | null>(
    null
  );
  const [voting, setVoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Build API payload from votes map */
  function buildVotesArray(votes: Map<number, VoteChoice>) {
    return Array.from(votes.entries()).map(([matchIndex, winner]) => {
      const match = readyMatches[matchIndex];
      if (!match) throw new Error(`Match not found at index: ${matchIndex}`);

      const [resultAId, resultBId] = match;
      return {
        arena_run_id: runId!,
        result_a_id: resultAId,
        result_b_id: resultBId,
        choice: winner,
        user_email: user?.email,
      };
    });
  }

  /** Submit votes to server, optionally navigating to results on success */
  async function submitVotes(
    votes: Map<number, VoteChoice>,
    navigateOnSuccess = false
  ) {
    if (votes.size === 0 || submitting || !runId) return;

    setSubmitting(true);
    try {
      const votesArray = buildVotesArray(votes);
      await apiRequest('/api/arena/votes/batch', {
        method: 'POST',
        body: JSON.stringify({ votes: votesArray }),
      });

      setPendingVotes(new Map());
      if (navigateOnSuccess) {
        navigate(`/arena/${runId}/results`);
      }
    } catch (err: any) {
      logError('Vote submission error', err);
      setError(
        "We couldn't save your votes. Please try again or contact support if the problem persists."
      );
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  /** Submit all pending votes (called from "Finish & View Results" button) */
  async function submitAllVotes() {
    return submitVotes(pendingVotes);
  }

  function handleVote(winner: VoteChoice) {
    if (!currentMatch || voting) return;
    setCurrentSelection(winner);
  }

  function handleNext() {
    if (!currentMatch) return;

    const isLastMatch = currentIndex === effectiveTotalMatches - 1;

    if (currentSelection) {
      if (isLastMatch) {
        // Last match - include current selection and submit all votes
        const finalVotes = new Map(pendingVotes);
        finalVotes.set(currentIndex, currentSelection);
        submitVotes(finalVotes, true);
      } else {
        // Not last match - store vote and advance
        setPendingVotes(prev => {
          const newVotes = new Map(prev);
          newVotes.set(currentIndex, currentSelection);
          return newVotes;
        });

        markVoted(currentIndex);
        setCurrentSelection(null);

        const nextIndex = currentIndex + 1;
        if (nextIndex < effectiveTotalMatches) {
          goTo(nextIndex);
        }
      }
    } else if (canGoNext) {
      next();
    }
  }

  return {
    currentSelection,
    voting,
    submitting,
    error,
    setError,
    handleVote,
    handleNext,
    submitAllVotes,
  };
}
